import React, { useState, useEffect } from 'react';
import './App.css';
import { apiFetch } from './api';
import { fetchCompanyLogo } from './companyLogo';
import { useAuth } from './auth/AuthProvider';
import { isAuthEnabled } from './auth/msalConfig';
import type { AppTab } from './auth/roles';
import Navigation from './components/Navigation';
import UserAccountMenu from './components/UserAccountMenu';
import Resources from './components/Resources';
import Forecast from './components/Forecast';
import Report from './components/Report';
import Settings from './components/Settings';
import HomeMonthlyRecap from './components/HomeMonthlyRecap';
import HomeBatchStatus from './components/HomeBatchStatus';
import RoleSimulationBanner from './components/RoleSimulationBanner';

type Tab = AppTab;

function App() {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);

  // Vérifier que l'API répond (évite "Failed to fetch" sans explication)
  useEffect(() => {
    apiFetch('/api/env-check')
      .then((res) => setApiReachable(res.ok))
      .catch(() => setApiReachable(false));
  }, []);

  // Rediriger si l'onglet actif n'est pas autorisé pour le rôle
  useEffect(() => {
    if (!isAuthEnabled() || !auth || auth.accessLoading) return;
    if (!auth.canTab(activeTab)) {
      const fallback: Tab[] = ['home', 'resources', 'forecast', 'report', 'settings'];
      const allowed = fallback.find((t) => auth.canTab(t));
      if (allowed) setActiveTab(allowed);
    }
  }, [auth, activeTab]);

  // Charger le logo partagé depuis l'API
  useEffect(() => {
    fetchCompanyLogo().then((url) => {
      if (url) setLogoUrl(url);
    });
  }, []);

  const handleLogoChange = (newLogoUrl: string) => {
    setLogoUrl(newLogoUrl || null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'resources':
        return <Resources />;
      case 'forecast':
        return <Forecast />;
      case 'report':
        return <Report />;
      case 'settings':
        return <Settings onLogoChange={handleLogoChange} currentLogo={logoUrl} />;
      case 'home':
      default:
        return <HomeMonthlyRecap />;
    }
  };

  return (
    <div className="App">
      <a className="skip-to-content" href="#app-main-content">
        Aller au contenu principal
      </a>
      <RoleSimulationBanner />
      {apiReachable === false && (
        <div className="app-alert app-alert--error" role="alert">
          Le serveur API ne répond pas. Lancez-le sur le port 3000 (ex.{' '}
          <code>npm run server</code> dans un terminal à la racine du projet).
        </div>
      )}
      <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <aside className="app-sidebar" data-testid="app-sidebar">
          <div className="app-sidebar-header">
            <div className="logo-container app-sidebar-logo">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo Anima Néo" className="uploaded-logo" />
              ) : (
                <>
                  <div className="logo">AN</div>
                  <div className="logo-text">Anima Néo</div>
                </>
              )}
            </div>
            {isAuthEnabled() && auth && (
              <div className="app-sidebar-user">
                <UserAccountMenu
                  compact={sidebarCollapsed}
                  displayName={auth.displayName}
                  email={auth.email}
                  roleLabel={auth.roleLabel}
                  onLogout={() => auth.logout()}
                />
              </div>
            )}
          </div>
          <Navigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isCollapsed={sidebarCollapsed}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
            canTab={auth?.canTab}
          />
          <div className="app-sidebar-footer" data-testid="app-sidebar-footer">
            <HomeBatchStatus />
          </div>
        </aside>
        <section className="app-content" id="app-main-content" aria-live="polite">
          {renderContent()}
        </section>
      </div>
    </div>
  );
}

export default App;
